import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { message } from 'antd';
import { useDebounceFn } from 'ahooks';

import { Button, Form, Input } from '@fuxi/eevee-ui';
import { SubmitSuccess } from '@portalCommon/components/SubmitSuccess';
import { useNECaptcha } from '@yaotai/common-utils';
import {
  POINT_BURIAL_MAP_PORTAL,
  PointBurLEnumPortal,
  getPostBodyV3,
  pageViewDouYinTracker,
  reportContactCommitFrom,
} from '@portalCommon/utils';

import { sendTrackerClickEvent } from '@commonPackage/utils';
import QS from 'query-string';
import { useLocation } from 'react-router-dom';
import classnames from 'classnames/bind';
import { useReferralCode } from '@portalCommon/hooks';
import { ReferralInvalidToast } from '@portalCommon/components';
import ContactFormItems from '@/components/ContactFormItems';
import QndAFormItems from '@/components/QndAFormItems';
import API from '@/services';

import styles from './index.module.less';

const cx = classnames.bind(styles);

const buttonLayout = {
  wrapperCol: { span: 24, offset: 8 },
};

export default function Consult({ className }: { className?: string }) {
  const { t } = useTranslation();
  const { search } = useLocation();
  const { code, isFromReferral } = useReferralCode();
  const [messageApi, messageContextHolder] = message.useMessage();

  const [form] = Form.useForm();
  const { page_name, key } = POINT_BURIAL_MAP_PORTAL[PointBurLEnumPortal.ContactBtnSubmit];
  const { element_name_from } = QS.parse(search);

  const [isContactSuccess, setIsContactSuccess] = useState<boolean | undefined>();
  const { captchaIns, getCaptchaIns } = useNECaptcha({
    mode: 'bind',
    element: '#captcha_activity',
    onVerify: function (err: any, data: any) {
      if (err) return;
      handleFinish(data.validate);
    },
  });

  // 抖音上报
  const submitDouYinTracker = (data: any) => {
    try {
      (window as any)._baq.track('form', { assets_id: '1776187719173133', data });
    } catch (err) {
      console.error('抖音tracker上报错误', err);
    }
  };

  const handleFinish = async (validate: string) => {
    const values = await form.validateFields();

    const body = getPostBodyV3(values, 'activity_advisory');

    try {
      const {
        data: { show_referrer_invalid_tip },
      } = await API.contact.contact({ ...body, validate });
      show_referrer_invalid_tip && messageApi.warning(ReferralInvalidToast());

      setIsContactSuccess(true);
      reportContactCommitFrom();

      submitDouYinTracker(body);
      sendTrackerClickEvent({
        page_name,
        key,
        pathBtnId: Array.isArray(element_name_from) ? element_name_from[0] : element_name_from || undefined,
      });
    } catch (err) {
      setIsContactSuccess(false);
      getCaptchaIns()?.refresh();
    }
  };

  const handleCaptcha = async () => {
    await form.validateFields();
    captchaIns?.verify();
  };
  const { run } = useDebounceFn(
    () => {
      handleCaptcha?.();
    },
    {
      wait: 500,
    },
  );

  useEffect(() => {
    pageViewDouYinTracker();
  }, []);

  return (
    <>
      {isContactSuccess ? (
        <SubmitSuccess type="reserve" />
      ) : (
        <div className={cx('form-wrapper', className)}>
          <div className={cx('desc')}>{t('consultForm.desc1')}</div>
          <div className={cx('desc')}>{t('consultForm.desc2')}</div>
          <div className={cx('form-container')}>
            <Form layout="vertical" form={form}>
              <ContactFormItems form={form} isContactSuccess={isContactSuccess} />
              {isFromReferral ? (
                <Form.Item
                  label={t('consultForm.recommendCode')}
                  name="referrer_code"
                  validateFirst
                  initialValue={code}>
                  <Input disabled style={{ width: 856 }} />
                </Form.Item>
              ) : (
                <QndAFormItems />
              )}
              <Form.Item label={t('consultForm.content')} name="content" validateFirst initialValue={''}>
                <Input.TextArea
                  allowClear
                  style={{ width: 856 }}
                  placeholder={t('consultForm.contentPlaceHolder')}
                  autoSize={{ minRows: 3 }}
                  maxLength={500}
                  showCount
                />
              </Form.Item>
              <Form.Item {...buttonLayout}>
                <div id="captcha_activity"></div>
                <Button onClick={run} style={{ width: 300, height: 48, fontSize: 18, fontWeight: 600, marginTop: 16 }}>
                  {t('consultForm.btn')}
                </Button>
              </Form.Item>
            </Form>
          </div>
          <div className={cx('help')}>{t('consultForm.desc3')}</div>
        </div>
      )}
      {messageContextHolder}
    </>
  );
}
